import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, orderBy, doc, getDoc, setDoc } from 'firebase/firestore';
import { useUser } from '../UserContext';
import { db } from '../Firebase';
import { useNavigate } from 'react-router-dom';
import Header from '../Components/Header/Header';

function History() {
  const [quizSessions, setQuizSessions] = useState([]);
  const [sessionDetails, setSessionDetails] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [selectedSession, setSelectedSession] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [stats, setStats] = useState({
    totalSessions: 0,
    totalQuestions: 0,
    averageScore: 0,
    bestScore: 0,
    totalCorrect: 0
  });
  const { currentUser } = useUser();
  const navigate = useNavigate();

  // Test function to create a sample quiz session
  const createTestSession = async () => {
    if (!currentUser?.uid) {
      alert('Please log in first');
      return;
    }

    try {
      const sessionId = `test_quiz_${Date.now()}`;
      const testSession = {
        userId: currentUser.uid,
        startedAt: new Date(),
        completedAt: new Date(),
        totalQuestions: 5,
        score: 3,
        percentage: 60,
        quizType: 'direct',
        totalAnswered: 5
      };

      await setDoc(doc(db, 'quizSessions', sessionId), testSession);
      console.log('Test session created with ID:', sessionId);
      alert('Test session created! Refresh the page to see it.');
      
      // Refresh the data
      fetchQuizHistory();
    } catch (error) {
      console.error('Error creating test session:', error);
      alert('Error creating test session: ' + error.message);
    }
  };

  const fetchQuizHistory = async () => {
    console.log('Fetching quiz history for user:', currentUser?.uid); // Debug log
    
    if (!currentUser?.uid) {
      console.log('No current user found'); // Debug log
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      
      // First, let's test if we can access the collection at all
      console.log('Testing database access...'); // Debug log
      const testQuery = query(collection(db, 'quizSessions'));
      const testSnapshot = await getDocs(testQuery);
      console.log('Total quiz sessions in database:', testSnapshot.size); // Debug log
      
      if (testSnapshot.size > 0) {
        console.log('Sample session data:', testSnapshot.docs[0].data()); // Debug log
      }
      
      // Fetch all quiz sessions for the user
      const sessionsQuery = query(
        collection(db, 'quizSessions'),
        where('userId', '==', currentUser.uid)
        // Temporarily removing orderBy to test if it's causing issues
        // orderBy('startedAt', 'desc')
      );

      console.log('Executing query for quizSessions collection'); // Debug log
      const sessionsSnapshot = await getDocs(sessionsQuery);
      
      console.log('Query result - empty:', sessionsSnapshot.empty); // Debug log
      console.log('Query result - size:', sessionsSnapshot.size); // Debug log
      
      if (sessionsSnapshot.empty) {
        console.log('No quiz sessions found'); // Debug log
        setQuizSessions([]);
        setStats({
          totalSessions: 0,
          totalQuestions: 0,
          averageScore: 0,
          bestScore: 0,
          totalCorrect: 0
        });
        return;
      }

      const sessions = sessionsSnapshot.docs.map(doc => {
        const data = doc.data();
        console.log('Session data:', data); // Debug log
        return {
          id: doc.id,
          ...data,
          startedAt: data.startedAt?.toDate() || new Date(),
          completedAt: data.completedAt?.toDate() || null
        };
      });

      // Sort sessions by startedAt in descending order manually
      sessions.sort((a, b) => b.startedAt - a.startedAt);

      console.log('Processed sessions:', sessions); // Debug log
      setQuizSessions(sessions);

      // Calculate statistics
      const totalSessions = sessions.length;
      const totalQuestions = sessions.reduce((sum, session) => sum + (session.totalQuestions || 0), 0);
      const totalCorrect = sessions.reduce((sum, session) => sum + (session.score || 0), 0);
      const averageScore = totalSessions > 0 ? Math.round((totalCorrect / totalQuestions) * 100) : 0;
      const bestScore = sessions.length > 0 ? Math.max(...sessions.map(s => s.percentage || 0)) : 0;

      console.log('Calculated stats:', { totalSessions, totalQuestions, totalCorrect, averageScore, bestScore }); // Debug log

      setStats({
        totalSessions,
        totalQuestions,
        averageScore,
        bestScore,
        totalCorrect
      });

    } catch (error) {
      console.error('Error fetching quiz history:', error);
      // Add more detailed error logging
      if (error.code) {
        console.error('Firebase error code:', error.code);
      }
      if (error.message) {
        console.error('Firebase error message:', error.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchQuizHistory();
  }, [currentUser?.uid]);

  const fetchSessionDetails = async (sessionId) => {
    try {
      const answersQuery = query(
        collection(db, 'quizSessions', sessionId, 'answers')
      );
      const answersSnapshot = await getDocs(answersQuery);
      const answers = answersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate?.() || new Date()
      }));

      setSessionDetails(prev => ({
        ...prev,
        [sessionId]: answers
      }));
    } catch (error) {
      console.error('Error fetching session details:', error);
    }
  };

  const handleSessionClick = async (session) => {
    setSelectedSession(session);
    setShowDetails(true);
    
    if (!sessionDetails[session.id]) {
      await fetchSessionDetails(session.id);
    }
  };

  const getScoreColor = (percentage) => {
    if (percentage >= 80) return 'text-green-600 bg-green-100';
    if (percentage >= 60) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const getQuizTypeIcon = (type) => {
    return type === 'direct' ? (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ) : (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
      </svg>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 text-lg">Loading your quiz history...</p>
        </div>
      </div>
    );
  }

  return (
    <div><Header/>
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4">
      <div className="max-w-7xl mx-auto">
      

        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-800 mb-4">Quiz History</h1>
          <p className="text-gray-600 text-lg">Track your learning progress and performance</p>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-12">
          <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-blue-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Sessions</p>
                <p className="text-3xl font-bold text-gray-900">{stats.totalSessions}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-green-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Questions</p>
                <p className="text-3xl font-bold text-gray-900">{stats.totalQuestions}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-purple-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Correct</p>
                <p className="text-3xl font-bold text-gray-900">{stats.totalCorrect}</p>
              </div>
              <div className="p-3 bg-purple-100 rounded-full">
                <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-yellow-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Average Score</p>
                <p className="text-3xl font-bold text-gray-900">{stats.averageScore}%</p>
              </div>
              <div className="p-3 bg-yellow-100 rounded-full">
                <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-red-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Best Score</p>
                <p className="text-3xl font-bold text-gray-900">{stats.bestScore}%</p>
              </div>
              <div className="p-3 bg-red-100 rounded-full">
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Quiz Sessions List */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-800">Quiz Sessions</h2>
          </div>

          {quizSessions.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">No quiz sessions yet</h3>
              <p className="text-gray-600 mb-6">Start taking quizzes to see your history here!</p>
              <button 
                onClick={() => navigate('/quiz')}
                className="bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-3 px-6 rounded-lg transition-colors duration-200"
              >
                Take Your First Quiz
              </button>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {quizSessions.map((session) => (
                <div 
                  key={session.id} 
                  className="p-6 hover:bg-gray-50 cursor-pointer transition-colors duration-200"
                  onClick={() => handleSessionClick(session)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        {getQuizTypeIcon(session.quizType)}
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-800">
                          {session.quizType === 'direct' ? 'Direct Answer Quiz' : 'Multiple Choice Quiz'}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {session.totalQuestions} questions • {session.startedAt.toLocaleDateString()} at {session.startedAt.toLocaleTimeString()}
                        </p>
                        {session.completedAt && (
                          <p className="text-xs text-gray-500">
                            Completed in {Math.round((session.completedAt - session.startedAt) / 1000 / 60)} minutes
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getScoreColor(session.percentage || 0)}`}>
                        {session.score || 0}/{session.totalQuestions || 0} ({session.percentage || 0}%)
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {session.completedAt ? 'Completed' : 'In Progress'}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Session Details Modal */}
        {showDetails && selectedSession && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-bold text-gray-800">Session Details</h2>
                  <button 
                    onClick={() => setShowDetails(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="p-6">
                {/* Session Summary */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-blue-800 mb-2">Quiz Type</h3>
                    <p className="text-blue-600">{selectedSession.quizType === 'direct' ? 'Direct Answer' : 'Multiple Choice'}</p>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-green-800 mb-2">Score</h3>
                    <p className="text-green-600">{selectedSession.score || 0}/{selectedSession.totalQuestions || 0} ({selectedSession.percentage || 0}%)</p>
                  </div>
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-purple-800 mb-2">Duration</h3>
                    <p className="text-purple-600">
                      {selectedSession.completedAt 
                        ? `${Math.round((selectedSession.completedAt - selectedSession.startedAt) / 1000 / 60)} minutes`
                        : 'In Progress'
                      }
                    </p>
                  </div>
                </div>

                {/* Individual Answers */}
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Question Details</h3>
                <div className="space-y-4">
                  {sessionDetails[selectedSession.id]?.map((answer, index) => (
                    <div key={answer.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-3">
                        <h4 className="font-semibold text-gray-800">Question {index + 1}</h4>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          answer.isCorrect ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {answer.isCorrect ? 'Correct' : 'Incorrect'}
                        </span>
                      </div>
                      
                      <p className="text-gray-700 mb-3">{answer.question}</p>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="font-medium text-gray-600">Your Answer:</span>
                          <p className="text-gray-800">{answer.userAnswer}</p>
                        </div>
                        <div>
                          <span className="font-medium text-gray-600">Correct Answer:</span>
                          <p className="text-gray-800">{answer.correctAnswer}</p>
                        </div>
                      </div>

                      {answer.attempts > 1 && (
                        <div className="mt-3 p-3 bg-yellow-50 rounded-lg">
                          <p className="text-sm text-yellow-800">
                            <span className="font-medium">Attempts:</span> {answer.attempts}
                            {answer.previousAnswers?.length > 0 && (
                              <span className="ml-2">
                                Previous answers: {answer.previousAnswers.join(', ')}
                              </span>
                            )}
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
    </div>
  );
}

export default History;